import { useTranslations } from '@vocab/react';
import {
  Actions,
  Box,
  ButtonLink,
  Card,
  Column,
  Columns,
  Divider,
  Heading,
  IconArrow,
  Inline,
  Stack,
  Text,
  TextLink,
} from 'braid-design-system';

import {
  buildCommunityGuidelinesURI,
  Section,
  useConfig,
} from '@seek/libs-shared';
import type { AboutModel } from '../../../pages/about/aboutModel';
import { ReviewReply } from '../reviewRow/ReviewReply';
import { ReviewRowRatingDetails } from '../reviewRow/ReviewRowRatingDetails';
import { ReviewRowRatingSummary } from '../reviewRow/ReviewRowRatingSummary';
import {
  ReviewsEmptyState,
  ReviewsEmptyStateType,
} from '../reviewsEmptyState/ReviewsEmptyState';
import { ReviewsSummaryCard } from '../reviewsSummaryCard/ReviewsSummaryCard';
import translations from './.vocab';

type CompanyReviewSectionProps = {
  model: AboutModel;
  link?: string;
  onActiveTabChanged: (item: string | undefined) => void;
  trackReviewsClicked: () => void;
  trackWriteAReviewClicked: () => void;
};

const REVIEWS_TO_SHOW = 3;

export const ReviewsSummarySectionWithDetails = ({
  model,
  link,
  onActiveTabChanged,
  trackReviewsClicked,
  trackWriteAReviewClicked,
}: CompanyReviewSectionProps) => {
  const { t } = useTranslations(translations);
  const { locale, zone, experience } = useConfig();
  const communityGuidelineURL = buildCommunityGuidelinesURI({
    locale,
    zone,
    experience,
  });

  // Overall rating is only exist when the minimum review count is met
  // If there is no overall rating, show the empty state
  const isOverallRatingNotExist = !model?.reviewSummary?.overallRating;

  const { reviews } = model;
  const reviewsList = reviews?.reviews || [];
  const reviewsListToShow = reviewsList.slice(0, REVIEWS_TO_SHOW);

  return (
    <Section
      paddingBottom="none"
      heading={t('Title', { companyName: model.name })}
    >
      <Stack space="medium">
        {isOverallRatingNotExist ? (
          <Card>
            <ReviewsEmptyState
              companyName={model.name}
              wrapInCard={false}
              trackWriteAReviewClicked={trackWriteAReviewClicked}
              type={ReviewsEmptyStateType.NO_SUMMARY}
            />
          </Card>
        ) : (
          <ReviewsSummaryCard
            model={{
              employeeRecommendationRating:
                model.reviewSummary?.employeeRecommendationRating,
              overallRating: model.reviewSummary?.overallRating,
              ratingBreakdown: model.reviewSummary?.ratingBreakdown,
              salaryRating: model.reviewSummary?.salaryRating,
            }}
          />
        )}
        {reviewsListToShow?.length > 0 && (
          <>
            <Box paddingTop={'small'}>
              <Heading level="3">{t('Secondary title')}</Heading>
            </Box>
            <Stack space="small">
              {reviewsListToShow?.map((review, index) => (
                <Box id={`review-card-${index}`}>
                  <Card>
                    <Columns space="medium" collapseBelow="tablet">
                      <Column width="1/4">
                        <ReviewRowRatingSummary
                          model={review}
                          showBreakdown={false}
                        />
                      </Column>
                      <Column width="3/4">
                        <Stack space="large">
                          <ReviewRowRatingDetails
                            model={review}
                            liteTitleMode={true}
                            isReplyReviewAccessCodeValid={false}
                          />
                          {review.reply && (
                            <>
                              <Divider />
                              <ReviewReply reply={review.reply} />
                            </>
                          )}
                        </Stack>
                      </Column>
                    </Columns>
                  </Card>
                </Box>
              ))}
            </Stack>
            <Text align="left" size="xsmall">
              {t('Community guideline description', {
                companyName: model.name,
                TextLink: (v: string) => (
                  <TextLink href={communityGuidelineURL} rel="nofollow">
                    {v}
                  </TextLink>
                ),
              })}
            </Text>
            <Actions>
              <Inline space="none">
                <ButtonLink
                  href={link || '#'}
                  onClick={() => {
                    if (trackReviewsClicked) {
                      trackReviewsClicked();
                    }
                    if (onActiveTabChanged) {
                      onActiveTabChanged('reviews');
                    }
                  }}
                  icon={<IconArrow direction="right" />}
                  iconPosition="trailing"
                >
                  {t('See all reviews')}
                </ButtonLink>
              </Inline>
            </Actions>
          </>
        )}
      </Stack>
    </Section>
  );
};
